import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, ButtonGroup, Segment } from 'semantic-ui-react';
import Alert from '../../../../../utils/alert';
import ImportsRepo from '../../../../../utils/repository/importsRepo';
import useModalState from '../../../../../hooks/useModalState';
import {
    DescriptorCleanedLabel,
    DescriptorsToLabelsMapType,
    ExtractionType,
    FieldValueUpdateSpec,
    ImpactResponseType,
    ImpactType,
    ImportTableType,
    RowIdType,
} from '../../types';
import ApplyConfirmationModal from './components/applyConfirmationModal';
import LabelNormalizationModal from './components/labelNormalizationModal';
import Legend from './components/legend';
import Table from './components/table';
import { DEFAULT_IMPACT_VALUES, EVENTS, KEYS } from './constants';
import {
    useImportTableStoreDescriptorsEditMode,
    useImportTableStoreReportsEditMode,
    useImportTableStoreDescriptorActions,
    useImportTableStoreDescriptorsMap,
    useImportTableStoreReportActions,
    useImportTableStoreReportsErrors,
    useImportTableStoreReportsMap,
    useImportTableStoreCoordinatesActions,
    useImportTableStoreNavigationActiveCoordinates,
} from './context/importTableContext';
import ImpactModal from './components/impactModal';
import { RESPONSE_STATUS } from '../../../../../constnats/general';
import { IMPORT_DESCRIPTOR_SOURCE } from '../../constants/generalConstants';
import { getCleanedLabels } from './components/labelNormalizationModal/utils';
import BrowserStorage from '../../../../../utils/browserStorage';

type ImportTablePropsType = {
    extraction: ExtractionType;
    setImportTable: (importTable: ImportTableType) => void;
    handleDeleteImport: () => void;
    redirectBack: () => void;
};
const ImportTable = ({ extraction, setImportTable, handleDeleteImport, redirectBack }: ImportTablePropsType) => {
    const labelNormalizationModalState = useModalState();
    const applyModalState = useModalState();
    const impactModalState = useModalState();
    const [loading, setLoading] = useState(false);
    const [impact, setImpact] = useState<ImpactType>(DEFAULT_IMPACT_VALUES);
    const { activeRow, activeCol } = useImportTableStoreNavigationActiveCoordinates();
    const { setCoordinates: setCellCoordinates } = useImportTableStoreCoordinatesActions();
    const { cancelDescriptorsEditing, setDescriptorsLabels, disableDescriptorsEditMode } =
        useImportTableStoreDescriptorActions();
    const { cancelAllReportsEditing, disableAllReportsEditMode } = useImportTableStoreReportActions();
    const reportsMap = useImportTableStoreReportsMap();
    const descriptorsMap = useImportTableStoreDescriptorsMap();
    const descriptorsEditMode = useImportTableStoreDescriptorsEditMode();
    const reportsEditMode = useImportTableStoreReportsEditMode();
    const reportsErrors = useImportTableStoreReportsErrors();
    const [imagePopupOpen, setImagePopupOpen] = useState(false);
    const [hasPendingNormalization, setHasPendingNormalization] = useState(false);

    const nonMasterTableDescriptors = useMemo(
        () => Object.values(descriptorsMap).filter((d) => d.source !== IMPORT_DESCRIPTOR_SOURCE.MASTER_TABLE),
        [descriptorsMap]
    );
    const hasLabelsToNormalize = useMemo(
        () => !!getCleanedLabels(nonMasterTableDescriptors).length,
        [nonMasterTableDescriptors]
    );

    const haveLabelsBeenNormalized = BrowserStorage.getLabelsNormalizationStatus(extraction.id)

    const requiresLabelsNormalization = hasLabelsToNormalize && !haveLabelsBeenNormalized && !hasPendingNormalization;

    const handleCheckImpact = () => {
        ImportsRepo.checkImpact(extraction.id, (response: ImpactResponseType) => {
            if (response.status === RESPONSE_STATUS.SUCCESS) {
                setImpact(response.data);
            } else {
                Alert.error(`ERROR: ${response.message}`);
            }
        });
    };

    const handleOpenImpactModal = () => {
        handleCheckImpact();
        impactModalState.openModal();
    };

    const handleOpenApplyModal = () => {
        if (descriptorsEditMode || reportsEditMode) {
            Alert.warn('You have unsaved changes. Please either save or cancel the changes before applying!');
            return;
        }
        handleCheckImpact();
        applyModalState.openModal();
    };

    const handleSaveReports = useCallback(() => {
        setLoading(true);
        let hasErrors = false;

        for (const reportErrors of Object.values(reportsErrors)) {
            const hasReportErrors = Object.values(reportErrors).some((x) => x.error);
            if (hasReportErrors) {
                hasErrors = true;
                break;
            }
        }

        if (hasErrors) {
            Alert.warn('Cannot submit changes while incorrect values are present!');
            setLoading(false);
            return;
        }

        const reportsToSave = Object.values(reportsMap).map((report) => {
            return {
                id: report.id,
                rowIdToValue: Object.entries(reportsMap[report.id].rowIdToValue).reduce(
                    (acc: Record<RowIdType, FieldValueUpdateSpec>, [key, value]) => {
                        acc[key] = {
                            value: value.normalizedValue,
                            manuallyDisabledVerticalCalc: value.manuallyDisabledVerticalCalc,
                        };
                        return acc;
                    },
                    {}
                ),
            };
        });

        ImportsRepo.reportsBatchUpdate(extraction.id, { reportUpdates: reportsToSave }, (result: any) => {
            if (result.status === RESPONSE_STATUS.SUCCESS) {
                Alert.success('Reports updated successfully');
                disableAllReportsEditMode();
                setImportTable(result.data);
            } else {
                Alert.error(`ERROR: ${result.message}`);
            }
            setLoading(false);
        });
    }, [reportsMap, setLoading, disableAllReportsEditMode, setImportTable, extraction.id, reportsErrors]);

    const handleSaveDescriptors = useCallback(() => {
        setLoading(true);
        const descriptorsUpdateSpecs = Object.values(descriptorsMap).map((x) => ({
            descriptorId: x.id,
            inputScale: x.inputScale,
            label: x.label,
            dataType: x.dataType,
        }));

        ImportsRepo.descriptorsUpdate(extraction.id, { descriptorsUpdateSpecs }, (result: any) => {
            if (result.status === RESPONSE_STATUS.SUCCESS) {
                Alert.success('Fields updated successfully');
                disableDescriptorsEditMode();
                setImportTable(result.data);
                if (hasPendingNormalization) {
                    BrowserStorage.setLabelsNormalizationStatus(extraction.id, true)
                    setHasPendingNormalization(false);
                }
            } else {
                Alert.error(`ERROR: ${result.message}`);
            }
            setLoading(false);
        });
    }, [descriptorsMap, setLoading, disableDescriptorsEditMode, setImportTable, extraction.id]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (loading) {
                return;
            }
            if (event.key === KEYS.ENTER) {
                if (descriptorsEditMode) {
                    handleSaveDescriptors();
                }
                if (reportsEditMode) {
                    handleSaveReports();
                }
                setCellCoordinates({ activeRow: null, activeCol: null });
                setImagePopupOpen(false);
            } else if (event.key === KEYS.ESCAPE) {
                cancelAllReportsEditing();
                cancelDescriptorsEditing();
                setCellCoordinates({ activeRow: null, activeCol: null });
                setImagePopupOpen(false);
                setHasPendingNormalization(false);
            }
        };

        document.addEventListener(EVENTS.KEY_DOWN, handleKeyDown);
        return () => {
            document.removeEventListener(EVENTS.KEY_DOWN, handleKeyDown);
        };
    }, [
        descriptorsEditMode,
        reportsEditMode,
        handleSaveDescriptors,
        handleSaveReports,
        cancelAllReportsEditing,
        cancelDescriptorsEditing,
        loading,
        setCellCoordinates,
    ]);

    useEffect(() => {
        const handleKeyNavigation = (event: KeyboardEvent) => {
            const isModifierPressed = event.ctrlKey || event.metaKey; // Ctrl on Win/Linux, Command on macOS
            if (activeRow === null || activeCol === null || !isModifierPressed) {
                return;
            }
            if (event.key === KEYS.ARROW_UP) {
                setCellCoordinates({ activeRow: activeRow - 1, activeCol });
            } else if (event.key === KEYS.ARROW_RIGHT) {
                setCellCoordinates({ activeRow, activeCol: activeCol + 1 });
            } else if (event.key === KEYS.ARROW_DOWN) {
                setCellCoordinates({ activeRow: activeRow + 1, activeCol });
            } else if (event.key === KEYS.ARROW_LEFT) {
                setCellCoordinates({ activeRow, activeCol: activeCol - 1 });
            } else if (event.key === KEYS.LOWER_I) {
                setImagePopupOpen((prev) => !prev);
            }
        };

        document.addEventListener(EVENTS.KEY_DOWN, handleKeyNavigation);

        return () => {
            document.removeEventListener(EVENTS.KEY_DOWN, handleKeyNavigation);
        };
    }, [activeRow, activeCol, setCellCoordinates]);

    const handleApplyImport = () => {
        setLoading(true);
        applyModalState.closeModal();
        ImportsRepo.applyImport(extraction.id, (response: any) => {
            if (response.status === RESPONSE_STATUS.SUCCESS) {
                Alert.success('Import applied successfully');
                BrowserStorage.deleteLabelsNormalizationStatus(extraction.id)
                redirectBack();
            } else {
                Alert.error(`ERROR: ${response.message}`);
                setLoading(false);
            }
        });
    };

    const confirmDescriptorsLabelsCleanup = (cleanCandidates: DescriptorCleanedLabel[]) => {
        const descriptorsToLabels = cleanCandidates.reduce((acc, curr) => {
            acc[curr.descriptorId] = curr.cleanLabel;
            return acc;
        }, {} as DescriptorsToLabelsMapType);

        setDescriptorsLabels(descriptorsToLabels);
        labelNormalizationModalState.closeModal();
        setHasPendingNormalization(true);
    };

    return (
        <Segment>
            <Segment className="overflow--auto">
                <Table
                    extraction={extraction}
                    imagePopupOpen={imagePopupOpen}
                    requiresLabelsNormalization={requiresLabelsNormalization}
                />
                {descriptorsEditMode && (
                    <Button className="mt-05" size="mini" primary onClick={labelNormalizationModalState.openModal}>
                        Normalize Labels
                    </Button>
                )}
            </Segment>
            <ButtonGroup fluid widths={3}>
                <Button disabled={loading} primary onClick={handleOpenImpactModal}>
                    Check Impact
                </Button>
                <Button disabled={loading} positive onClick={handleOpenApplyModal}>
                    Apply
                </Button>
                <Button disabled={loading} negative onClick={handleDeleteImport}>
                    Delete
                </Button>
            </ButtonGroup>
            <Legend />
            {applyModalState.open && (
                <ApplyConfirmationModal
                    open={applyModalState.open}
                    closeModal={applyModalState.closeModal}
                    impact={impact}
                    onConfirm={handleApplyImport}
                />
            )}
            {labelNormalizationModalState.open && (
                <LabelNormalizationModal
                    open={labelNormalizationModalState.open}
                    descriptors={nonMasterTableDescriptors}
                    closeModal={labelNormalizationModalState.closeModal}
                    onConfirm={confirmDescriptorsLabelsCleanup}
                />
            )}
            {impactModalState.open && (
                <ImpactModal open={impactModalState.open} closeModal={impactModalState.closeModal} impact={impact} />
            )}
        </Segment>
    );
};

export default ImportTable;
/**
 * Extract this in a stable function inside the component with the params used from the component
 * something like :
 * // Create a stable callback for the recalculation
const performRecalculation = useCallback(() => {
  setIsLoadingRecalculation(true);
  recalculateApiCall(
    extraction.id,
    descriptorsState.descriptorsMap,
    reportsState,
    () => {
      setIsLoadingRecalculation(false);
    },
    () => {
      hasFollowupRecalculation.current = false;
      reportsStateRef.current = null;
      setIsLoadingRecalculation(false);
    }
  );
}, [extraction.id, descriptorsState.descriptorsMap]); // Include all dependencies

// Use a custom debounce hook instead of manual setTimeout
useEffect(() => {
  if (!shouldRecalculate || hasFollowupRecalculation.current || isLoadingRecalculation) {
    return;
  }
  
  // Debounce the recalculation
  const timer = setTimeout(performRecalculation, 1000);
  
  // Clean up function
  return () => clearTimeout(timer);
}, [shouldRecalculate, hasFollowupRecalculation.current, isLoadingRecalculation, performRecalculation]);
 */
// function recalculateApiCall(
//     importId: number,
//     descriptorsMap: DescriptorsMapType,
//     reportsMap: ReportsMapType,
//     successCallback: () => void,
//     errorCallback: () => void
// ) {
//     const descriptors = Object.values(descriptorsMap);
//     const reports = Object.values(reportsMap).map((r) => {
//         const { editing, ...report } = r;
//         return report;
//     });
//     const importTable: ImportTableType = {
//         descriptors,
//         reports,
//     };
//     console.log('fire request with data', reportsMap);
//     ImportsRepo.recalculateTableAPICall(
//         importId,
//         importTable,
//         () => {
//             // TODO: IMPORTANT: this timeout here is set for testing only
//             // it should be removed
//             setTimeout(() => {
//                 successCallback();
//             }, 4000);
//         },
//         () => {
//             errorCallback();
//         }
//     );
// }

// const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>();
// const hasFollowupRecalculation = useRef(false);
// const reportsStateRef = useRef<ReportsMapType | null>(null);
// const [isLoadingRecalculation, setIsLoadingRecalculation] = useState(false);

// useEffect(() => {
//     /**
//      * when something changes in the report state save it to a ref
//      * that will later be used for any followup calls
//      */
//     reportsStateRef.current = reportsState;
//     /**
//      * check if an action that warrants a recalculation has been triggered
//      * if no such action has yet been triggered no api call for recalculation should be made
//      */
//     if (!shouldRecalculate) {
//         return;
//     }

//     /**
//      * check if we have already marked that there is a follow recalculation that is to
//      * be executed after any existing recalculations complete
//      * if we do have another followup recalculation we should not make another request for
//      * recalculation
//      */
//     if (hasFollowupRecalculation.current) {
//         return;
//     }

//     /**
//      * check if we have a recalculation that is currently loading
//      * if we have an already existing recalculation that is running and the
//      * user triggers another recalculation action, we mark that there should be
//      * a follow up recalculation after this finishes.
//      */
//     if (isLoadingRecalculation) {
//         hasFollowupRecalculation.current = true;
//         return;
//     }

//     if (timerRef.current) {
//         clearTimeout(timerRef.current);
//     }
//     timerRef.current = setTimeout(() => {
//         setIsLoadingRecalculation(true);
//         recalculateApiCall(
//             extraction.id,
//             descriptorsState.descriptorsMap,
//             reportsState,
//             () => {
//                 setIsLoadingRecalculation(false);
//             },
//             () => {
//                 // error consumer
//                 hasFollowupRecalculation.current = false;
//                 reportsStateRef.current = null;
//                 setIsLoadingRecalculation(false);
//             }
//         );
//     }, 1 * 1000);

//     return () => {
//         if (timerRef.current) {
//             clearTimeout(timerRef.current);
//         }
//     };
// }, [reportsState, shouldRecalculate]);

// useEffect(() => {
//     /**
//      * if we already have a recalculation request that is currently being
//      * executed we wait for that request to finish before we execute the followup
//      */
//     if (isLoadingRecalculation) {
//         return;
//     }

//     /**
//      * check to see if we have a followup request and state
//      * if we don't have either state or followup request
//      * a recalculation request should no be made
//      */
//     if (hasFollowupRecalculation.current && reportsStateRef.current) {
//         /**
//          * mark that a recalculation request has been triggered
//          */
//         setIsLoadingRecalculation(true);
//         /**
//          * trigger the follow up request
//          */
//         recalculateApiCall(
//             extraction.id,
//             descriptorsState.descriptorsMap,
//             reportsStateRef.current,
//             () => {
//                 setIsLoadingRecalculation(false);
//             },
//             () => {
//                 // error consumer
//                 hasFollowupRecalculation.current = false;
//                 reportsStateRef.current = null;
//                 setIsLoadingRecalculation(false);
//             }
//         );
//         /**
//          * since the follow up request has been triggered
//          * we clear the boolean signifying that there are no more followup requests
//          *
//          * NOTE while the followup request is being made and since there are no more followup
//          * requests we now allow while this request is running for more followup requests
//          * to be triggered after this one giving us the ability to follow up on the user actions
//          */
//         hasFollowupRecalculation.current = false;
//     }
// }, [isLoadingRecalculation]);
